import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import ProtectedRoute from './components/ProtectedRoute'
const LoginScreen = lazy(() => import('./screens/LoginScreen'))
const ResetPasswordScreen = lazy(() => import('./screens/ResetPasswordScreen'))
const CameraScreen = lazy(() => import('./screens/CameraScreen'))
const AnimalsScreen = lazy(() => import('./screens/AnimalsScreen'))
const IdentifyingScreen = lazy(() => import('./screens/IdentifyingScreen'))
const ResultScreen = lazy(() => import('./screens/ResultScreen'))
const HistoricoScreen = lazy(() => import('./screens/HistoricoScreen'))
const EmergenciaScreen = lazy(() => import('./screens/EmergenciaScreen'))
const PerfilScreen = lazy(() => import('./screens/PerfilScreen'))
const WhatsAppPreviewScreen = lazy(() => import('./screens/WhatsAppPreviewScreen'))
const PesquisarScreen = lazy(() => import('./screens/PesquisarScreen'))
const FichaPlantaScreen = lazy(() => import('./screens/FichaPlantaScreen'))
const HerbarioBeraldoScreen = lazy(() => import('./screens/HerbarioBeraldoScreen'))
const SegurasScreen = lazy(() => import('./screens/SegurasScreen'))
import OnboardingScreen, { isOnboardingDone } from './screens/OnboardingScreen'
import NameModal from './components/NameModal'
import { useTheme } from './lib/use-theme'

// B: feedback imediato enquanto o pacote da tela carrega (era fallback={null} = branco/travado)
function ScreenFallback() {
  return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div className="fs-spin" role="status" aria-label="Carregando" />
    </div>
  )
}

export default function App() {
  useTheme()

  // A: pré-carrega os pacotes das telas principais quando o app fica ocioso →
  // transição instantânea depois. Pula em economia de dados / conexão 2G.
  useEffect(() => {
    const conn = (navigator as unknown as { connection?: { saveData?: boolean; effectiveType?: string } }).connection
    if (conn?.saveData) return
    if (conn?.effectiveType && /2g/.test(conn.effectiveType)) return
    const warm = () => {
      import('./screens/HistoricoScreen')
      import('./screens/PesquisarScreen')
      import('./screens/EmergenciaScreen')
      import('./screens/PerfilScreen')
      import('./screens/CameraScreen')
    }
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
      cancelIdleCallback?: (id: number) => void
    }
    let idleId: number | undefined
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    if (w.requestIdleCallback) idleId = w.requestIdleCallback(warm, { timeout: 3000 })
    else timeoutId = setTimeout(warm, 1500)
    return () => {
      if (idleId !== undefined && w.cancelIdleCallback) w.cancelIdleCallback(idleId)
      if (timeoutId !== undefined) clearTimeout(timeoutId)
    }
  }, [])

  const rootRedirect = isOnboardingDone() ? '/login' : '/onboarding'

  return (
    <>
      <NameModal />
      <Suspense fallback={<ScreenFallback />}>
      <Routes>
      <Route path="/" element={<Navigate to={rootRedirect} replace />} />
      <Route path="/onboarding" element={<OnboardingScreen />} />
      <Route path="/login" element={<LoginScreen />} />
      <Route path="/reset-password" element={<ResetPasswordScreen />} />

      {/* Rotas protegidas (só entra se autenticado) */}
      <Route
        path="/camera"
        element={
          <ProtectedRoute>
            <CameraScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/animais"
        element={
          <ProtectedRoute>
            <AnimalsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/identificando"
        element={
          <ProtectedRoute>
            <IdentifyingScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/resultado/:id"
        element={
          <ProtectedRoute>
            <ResultScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/historico"
        element={
          <ProtectedRoute>
            <HistoricoScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/emergencia"
        element={
          <ProtectedRoute>
            <EmergenciaScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/perfil"
        element={
          <ProtectedRoute>
            <PerfilScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracoes"
        element={
          <ProtectedRoute>
            <PerfilScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path="/whatsapp-preview"
        element={
          <ProtectedRoute>
            <WhatsAppPreviewScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pesquisar"
        element={
          <ProtectedRoute>
            <PesquisarScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path="/herbario-beraldo"
        element={
          <ProtectedRoute>
            <HerbarioBeraldoScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path="/seguras/:animal"
        element={
          <ProtectedRoute>
            <SegurasScreen />
          </ProtectedRoute>
        }
      />

      <Route
        path="/ficha/:id"
        element={
          <ProtectedRoute>
            <FichaPlantaScreen />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      </Suspense>
    </>
  )
}
